import {type ChartAggOption} from 'src/generated/graphql';

import {type RunHistoryRow, type RunKeyInfo} from '../../types/run';
import {type DataDogPanelType} from '../../util/panelTypes';
import {type ChartAreaOption} from '../../util/plotHelpers/chartTypes';
import {type SingleQuery} from '../../util/queryTypes';
import {type PrimaryLineType} from '../../util/rampFeatureFlags/linePlotDynamicConfigs';
import type * as RunTypes from '../../util/runTypes';

type EMAAggregation = {
  type: 'ema';
  parameters: {
    alpha: number;
    zeroDebias?: boolean;
  };
};

type GaussianAggregation = {
  type: 'gaussian';
  parameters: {
    sigma: number;
  };
};

type TimeWeightedEMAAggregation = {
  type: 'time_weighted_ema';
  parameters: {
    alpha: number;
  };
};

type RunningAverageAggregation = {
  type: 'running_average';
  parameters: {
    windowSize: number;
  };
};

export type MetricTransformation =
  | EMAAggregation
  | GaussianAggregation
  | TimeWeightedEMAAggregation
  | RunningAverageAggregation;

export type HistorySpec = {
  keys: string[];
  samples: number;
  minStep?: number;
  maxStep?: number;
};

export type BucketedHistorySpec = {
  keys: string[];
  bins: number;
  samples?: number;
  xAxis?: string;
  minStep?: number;
  maxStep?: number;
  minX?: number;
  maxX?: number;
  groupAgg?: ChartAggOption;
  groupArea?: ChartAreaOption;

  // key is the metric key, value is the preprocessing aggregations for that metric
  metricTransformations?: {[key: string]: MetricTransformation[]};
};

export type StepAxisBHS = BucketedHistorySpec & {
  xAxis: '_step';
  minX: never;
  maxX: never;
};
export type CustomAxisBHS = BucketedHistorySpec & {
  xAxis: Exclude<string, '_step'>;
  minX?: number;
  maxX?: number;
};

export function specIsStepAxis(spec: BucketedHistorySpec): spec is StepAxisBHS {
  return spec.xAxis === '_step';
}

// This type represents the configuration of some scope (like a panel) which may apply across multiple individual queries (typically a query per runset, i.e. `runsetQueries`).
export interface QueryConfig {
  // level?: 'run' | 'subgroup' | 'group';
  aggregationKeys?: string[];
  bucketedHistorySpecs?: BucketedHistorySpec[];
  // Selected Y metrics and Y-expression dependencies, excluding X-only metrics.
  yMetricNames?: string[];
  configKeys?: string[];
  disabled?: boolean;
  displayName?: string;
  enableArtifactCounts?: boolean;
  enableBasic?: boolean;
  enableSystemMetrics?: boolean;
  enableTags?: boolean;
  entityName: string;
  excludeOutliers?: boolean;
  fullAggregations?: boolean;
  fullConfig?: boolean;
  fullSummary?: boolean;
  groupAgg?: ChartAggOption;
  groupKeys?: string[];
  groupingDepth?: number;
  stabilizeRunGroups?: boolean;
  history?: boolean;
  historyFields?: string[];
  historyKeyInfo?: boolean;
  historySamples?: number;
  historySpecs?: HistorySpec[];
  internalProjectId?: string | undefined;
  keysLoading: boolean;
  page?: {size: number};
  panelType: DataDogPanelType;
  pollInterval?: number;
  projectName: string;
  runName?: string;
  runsetQueries: SingleQuery[];
  select?: string;
  summaryKeys?: string[];
  primaryLineType?: PrimaryLineType;
  wandbKeys?: string[];
  xAxisScale?: 'log'; // the x-axis scale notifies the backend that adjustments to sketching are required
}

export type RunWithRunsetInfo = RunTypes.Run & {
  runsetInfo: {
    id: string;
    name?: string;
  };
};

export function isRunWithRunsetInfo(r: RunTypes.Run): r is RunWithRunsetInfo {
  return (
    'runsetInfo' in r &&
    typeof (r as any).runsetInfo?.id === 'string' &&
    ['string', 'undefined'].indexOf(typeof (r as any).runsetInfo?.name) !== -1
  );
}

// Data holds results.
export interface Data {
  initialLoading: boolean;
  loading: boolean;
  entityName: string;
  projectName: string;
  runName?: string;
  displayName?: string;
  filtered: RunWithRunsetInfo[];
  filteredRunsById: {[key: string]: RunWithRunsetInfo};
  keyInfo: RunKeyInfo;
  totalRuns: number;
  limit?: number;
  histories: {
    data: Array<{
      name: string;
      displayName: string;
      history: RunHistoryRow[];
    }>;
  };
  doRunSetGrouping?: boolean;
  fetchedRunCount?: number;
  fetchedRunCountByRunsetId?: Record<string, number>;
  hasSubsampledGroups?: boolean;
  totalGroupedIncludedRuns?: number;
  loadMore?(page: number): void;
}
